import { useEffect, useState } from 'react';

// One in-memory copy of each dataset shared across all hook callers so
// switching steps / role doesn't re-fetch the 2 MB high-schools JSON.
let highSchoolsCache = null;
let collegesCache = null;
let highSchoolsPromise = null;
let collegesPromise = null;

function loadHighSchools() {
  if (highSchoolsCache) return Promise.resolve(highSchoolsCache);
  if (highSchoolsPromise) return highSchoolsPromise;
  highSchoolsPromise = fetch('/data/high-schools.json')
    .then((r) => r.json())
    .then((raw) => {
      // Mirror iOS SchoolData decoder: source uses {n,c,s,z} abbreviations.
      highSchoolsCache = raw.map((e) => ({
        name: e.n || '',
        city: (e.c || '').replace(/\b\w/g, (l) => l.toUpperCase()),
        state: (e.s || '').toUpperCase(),
        zip: e.z || '',
      }));
      return highSchoolsCache;
    })
    .catch(() => []);
  return highSchoolsPromise;
}

function loadColleges() {
  if (collegesCache) return Promise.resolve(collegesCache);
  if (collegesPromise) return collegesPromise;
  collegesPromise = fetch('/data/colleges.json')
    .then((r) => r.json())
    .then((raw) => {
      // iOS CollegeData decoder: {u,n,c,s,w,d} → unitid/name/city/state/website/division.
      collegesCache = raw.map((e) => ({
        unitid: e.u,
        name: e.n || '',
        city: e.c || '',
        state: e.s || '',
        websiteUrl: e.w,
        division: e.d,
      }));
      return collegesCache;
    })
    .catch(() => []);
  return collegesPromise;
}

// Shared by every school box: typed text shorter than this searches nothing,
// and no box shows more than MAX_SCHOOL_RESULTS suggestions. "st." is three
// characters, so it counts.
export const MIN_SCHOOL_QUERY_CHARS = 3;
// The list scrolls, so this is a ceiling, not a page size. Signup asks for the
// school BEFORE the state, so "st francis" (41 matches, 3 in one state) has to
// list every match: a short cap makes the right school unreachable.
export const MAX_SCHOOL_RESULTS = 100;

/**
 * React hook exposing the bundled school dataset.
 * Pass role="coach" to include colleges + high schools, anything else for
 * high-schools only (matches iOS RegisterView.InfoStep.search behavior).
 */
export function useSchoolData(role = 'player') {
  const [highSchools, setHighSchools] = useState(highSchoolsCache || []);
  const [colleges, setColleges] = useState(collegesCache || []);

  useEffect(() => {
    let cancelled = false;
    loadHighSchools().then((data) => {
      if (!cancelled) setHighSchools(data);
    });
    if (role === 'coach') {
      loadColleges().then((data) => {
        if (!cancelled) setColleges(data);
      });
    }
    return () => {
      cancelled = true;
    };
  }, [role]);

  /**
   * Coach: colleges first then high schools, capped at 15 combined.
   * Player: high schools only.
   */
  const search = (query, maxResults = MAX_SCHOOL_RESULTS, { preferState } = {}) => {
    const q = (query || '').trim().toLowerCase();
    if (q.length < MIN_SCHOOL_QUERY_CHARS) return [];
    if (role === 'coach') {
      const collegeMatches = filterAndRank(colleges, q, 8, 'college', preferState);
      const highSchoolMatches = filterAndRank(highSchools, q, 8, 'school', preferState);
      return [...collegeMatches, ...highSchoolMatches].slice(0, maxResults);
    }
    return filterAndRank(highSchools, q, maxResults, 'school', preferState);
  };

  /**
   * Unique exact-name lookup used to enrich freeform-typed names at submit
   * time. Returns null on collision so the backend never receives a wrong
   * city/state for a name that's ambiguous across states.
   */
  const exactMatch = (name) => {
    const needle = (name || '').trim().toLowerCase();
    if (!needle) return null;
    const pool = role === 'coach' ? [...colleges, ...highSchools] : highSchools;
    let found = null;
    for (const entry of pool) {
      if ((entry.name || '').toLowerCase() === needle) {
        if (found) return null;
        found = entry;
      }
    }
    return found;
  };

  return { search, exactMatch, loaded: highSchools.length > 0 };
}

// "St", "St." and "Saint" are the same word to a person typing a school name
// and three different strings in the data (695 / 63 names begin St / Saint).
// Normalise both sides the same way: lowercase, strip accents and punctuation,
// fold saint -> st, collapse whitespace.
function normalize(s) {
  return (s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[.,'\u2019()/&-]+/g, ' ')
    .replace(/\bsaint\b/g, 'st')
    .replace(/\s+/g, ' ')
    .trim();
}

// Normalised copies are built once per entry, not once per keystroke (43k rows).
const normCache = new WeakMap();
function norm(entry) {
  let n = normCache.get(entry);
  if (!n) {
    const name = normalize(entry.name);
    n = {
      name,
      nameWords: name.split(' ').filter(Boolean),
      cityWords: normalize(entry.city).split(' ').filter(Boolean),
      state: (entry.state || '').toLowerCase(),
    };
    normCache.set(entry, n);
  }
  return n;
}

// `done` = the typed word was terminated (trailing space or period), so "st. "
// means the word "st", not the prefix of "star".
const wordStarts = (words, t, done) => words.some((w) => (done ? w === t : w.startsWith(t)));

/**
 * Rank, don't take the first N. Tiers (lower is better):
 *   0  the name starts with what was typed
 *   1  every typed word starts a word of the name
 *   2  every typed word starts a word of the name, city or state — so
 *      "st patrick il" narrows by state
 *   3  the typed text appears inside the name
 * Within a tier a school in `preferState` (the athlete's home state) comes
 * first -- there are 40 St. Francis schools and 3 are in Illinois -- then the
 * shorter name wins (St. Patrick before St. Patrick-St.
 * Vincent Regional High School), then alphabetical. Every match is scored
 * before the cap is applied: stopping at the first N in file order is why
 * "st. " used to fill with Alaska and never reach Illinois.
 */
function filterAndRank(pool, rawQuery, max, type, preferState) {
  const home = (preferState || '').toLowerCase();
  const q = normalize(rawQuery);
  if (q.length < 2) return [];
  const tokens = q.split(' ');
  const done = /[\s.]$/.test(rawQuery || '');
  const last = tokens.length - 1;
  const hit = (words, t, i) => wordStarts(words, t, done && i === last);
  const scored = [];
  for (const entry of pool) {
    const n = norm(entry);
    let tier;
    if (done ? n.name === q || n.name.startsWith(q + ' ') : n.name.startsWith(q)) tier = 0;
    else if (tokens.every((t, i) => hit(n.nameWords, t, i))) tier = 1;
    else if (tokens.every((t, i) => hit(n.nameWords, t, i) || hit(n.cityWords, t, i) || n.state === t)) tier = 2;
    else if (!done && n.name.includes(q)) tier = 3;
    else continue;
    scored.push({ entry, tier, away: home && n.state !== home ? 1 : 0, len: n.name.length });
  }
  scored.sort((a, b) => a.tier - b.tier || a.away - b.away || a.len - b.len || (a.entry.name < b.entry.name ? -1 : 1));
  return scored.slice(0, max).map((s) => tagged(s.entry, type));
}

function tagged(entry, type) {
  return {
    ...entry,
    sourceType: type, // 'school' | 'college'
    id: type === 'college' ? `col:${entry.unitid}` : `hs:${entry.name}|${entry.city}|${entry.state}|${entry.zip}`,
    subtitle: [entry.city, entry.state].filter(Boolean).join(', '),
  };
}
